import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

export interface DropdownOption {
  value: string;
  label: string;
  hint?: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (v: string) => void;
  placeholder?: string;
  width?: number | string;
  align?: 'left' | 'right';
}

// The panel is rendered into document.body via a portal so it escapes any
// `overflow: hidden` parent (e.g. the mobile Reports card). Position is
// computed from the trigger's bounding rect on each open and re-recomputed
// when the user scrolls or resizes the viewport while the panel is open.
export function Dropdown({
  value, options, onChange,
  placeholder = 'Select…', width = 180, align = 'left',
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef   = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Reposition: anchor below the trigger, align left or right of it.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const compute = () => {
      const r = triggerRef.current!.getBoundingClientRect();
      const minPanelWidth = typeof width === 'number' ? width + 20 : Math.max(r.width, 180);
      const panelWidth = Math.max(minPanelWidth, r.width);
      const left = align === 'right'
        ? Math.max(8, r.right - panelWidth)
        : Math.min(window.innerWidth - panelWidth - 8, r.left);
      setPos({ top: r.bottom + 4, left, width: panelWidth });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open, align, width]);

  // Click-outside / escape close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find(o => o.value === value);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="filter-chip"
        onClick={() => setOpen(v => !v)}
        style={{ minWidth: width, width: width === '100%' ? '100%' : undefined }}
      >
        {current?.label || placeholder}
        <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" style={{ opacity: .6 }}>
          <path d="M2 4l4 4 4-4" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div
          ref={panelRef}
          className="menu"
          style={{
            position: 'fixed',
            top: pos.top, left: pos.left,
            minWidth: pos.width,
            zIndex: 1000,
          }}
        >
          {options.map(opt => (
            <button
              key={opt.value}
              className={`menu-item ${value === opt.value ? 'is-active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {value === opt.value ? (
                <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : <span style={{ width: 12 }} />}
              <span className="flex-1 text-truncate">{opt.label}</span>
              {opt.hint && <span className="text-xs text-subtle">{opt.hint}</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
